import { createContext, useContext, useReducer, useCallback, ReactNode } from 'react'
import type {
  DashboardOverallStats,
  DashboardProgressItem,
  DashboardActionItem,
} from '../types/dashboard.types'
import { buildLocalDashboardData } from '@/services/dreamityDashboardFromLocal'

interface DashboardState {
  overallStats: DashboardOverallStats
  progressItems: DashboardProgressItem[]
  actionItems: DashboardActionItem[]
  loading: boolean
  error: string | null
  dateRange: { startDate: string | null; endDate: string | null }
}

type DashboardAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_OVERALL_STATS'; payload: DashboardOverallStats }
  | { type: 'SET_PROGRESS_ITEMS'; payload: DashboardProgressItem[] }
  | { type: 'SET_ACTION_ITEMS'; payload: DashboardActionItem[] }
  | { type: 'SET_DATE_RANGE'; payload: { startDate: string; endDate: string } }

interface DashboardContextType extends DashboardState {
  fetchStats: () => void
  fetchProgress: () => void
  fetchActionItems: (startDate: string, endDate: string) => void
  fetchAllDashboardData: (startDate: string, endDate: string) => void
}

const DashboardContext = createContext<DashboardContextType | undefined>(undefined)

const initialState: DashboardState = {
  overallStats: {
    totalDreamCount: 0,
    totalPlanCount: 0,
    totalItemCount: 0,
    totalBudget: 0,
  },
  progressItems: [],
  actionItems: [],
  loading: false,
  error: null,
  dateRange: { startDate: null, endDate: null },
}

function dashboardReducer(state: DashboardState, action: DashboardAction): DashboardState {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload }
    case 'SET_ERROR':
      return { ...state, error: action.payload }
    case 'SET_OVERALL_STATS':
      return { ...state, overallStats: action.payload }
    case 'SET_PROGRESS_ITEMS':
      return { ...state, progressItems: action.payload }
    case 'SET_ACTION_ITEMS':
      return { ...state, actionItems: action.payload }
    case 'SET_DATE_RANGE':
      return {
        ...state,
        dateRange: { startDate: action.payload.startDate, endDate: action.payload.endDate },
      }
    default:
      return state
  }
}

interface DashboardProviderProps {
  children: ReactNode
}

export function DashboardProvider({ children }: DashboardProviderProps) {
  const [state, dispatch] = useReducer(dashboardReducer, initialState)

  const applyLocalBuild = useCallback((startDate: string, endDate: string) => {
    dispatch({ type: 'SET_ERROR', payload: null })
    const { overall, progress, actions } = buildLocalDashboardData(startDate, endDate)
    dispatch({ type: 'SET_OVERALL_STATS', payload: overall })
    dispatch({ type: 'SET_PROGRESS_ITEMS', payload: progress })
    dispatch({ type: 'SET_ACTION_ITEMS', payload: actions })
    dispatch({ type: 'SET_DATE_RANGE', payload: { startDate, endDate } })
  }, [])

  const fetchStats = useCallback(() => {
    const t = new Date().toISOString().split('T')[0]
    applyLocalBuild(t, t)
  }, [applyLocalBuild])

  const fetchProgress = useCallback(() => {
    const t = new Date().toISOString().split('T')[0]
    applyLocalBuild(t, t)
  }, [applyLocalBuild])

  const fetchActionItems = useCallback(
    (startDate: string, endDate: string) => {
      applyLocalBuild(startDate, endDate)
    },
    [applyLocalBuild]
  )

  const fetchAllDashboardData = useCallback(
    (startDate: string, endDate: string) => {
      applyLocalBuild(startDate, endDate)
    },
    [applyLocalBuild]
  )

  return (
    <DashboardContext.Provider
      value={{
        ...state,
        fetchStats,
        fetchProgress,
        fetchActionItems,
        fetchAllDashboardData,
      }}
    >
      {children}
    </DashboardContext.Provider>
  )
}

export function useDashboardContext() {
  const context = useContext(DashboardContext)
  if (context === undefined) {
    throw new Error('useDashboard must be used within a DashboardProvider')
  }
  return context
}
